import {
  CONFIGURE_OEM_SUPPORT_TICKET_TYPE_MUTATION,
  CREATE_OEM_SUPPORT_TICKET_TYPE_MUTATION,
  CURRENT_USER_QUERY,
  DELETE_OEM_SUPPORT_TICKET_TYPE_MUTATION,
  UPDATE_OEM_SUPPORT_TICKET_TYPE_MUTATION,
} from "#/src/api";
import { errorsHandler } from "#/src/transformers";
import { useMutation, type ApolloCache } from "@apollo/client";

export type SupportTicketTypeInput = {
  name: string;
  icon: string;
  color: string;
  isInternal?: boolean;
};

export type DeleteSupportTicketTypeInput = {
  transferToSupportTicketTypeId: string;
};

// Lists/kanban caches to drop whenever a support-ticket type changes, so the
// ticket views re-fetch with the new type set.
const evictSupportTicketCaches = (cache: ApolloCache<unknown>) => {
  ["listOwnOemSupportTickets", "listOwnOemKanbanSupportTickets"].forEach(
    (fieldName) => cache.evict({ id: "ROOT_QUERY", fieldName }),
  );
  cache.gc();
};

export const useCreateOemSupportTicketType = () => {
  const [create, { loading, error }] = useMutation(
    CREATE_OEM_SUPPORT_TICKET_TYPE_MUTATION,
  );

  return {
    loading,
    error,
    createTicketType: async (input: SupportTicketTypeInput) =>
      new Promise((resolve, reject) => {
        return create({
          variables: { input },
          update(cache, { data: { createOemSupportTicketType: ref } = {} }) {
            const { currentUser } =
              cache.readQuery({
                query: CURRENT_USER_QUERY,
              }) || {};

            if (currentUser) {
              cache.writeQuery({
                query: CURRENT_USER_QUERY,
                data: {
                  currentUser: {
                    ...currentUser,
                    oem: {
                      ...currentUser.oem,
                      supportTicketTypes: [
                        ...currentUser.oem.supportTicketTypes,
                        ref,
                      ],
                    },
                  },
                },
              });
              evictSupportTicketCaches(cache);
            }
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useUpdateOemSupportTicketType = () => {
  const [updateType, { loading, error }] = useMutation(
    UPDATE_OEM_SUPPORT_TICKET_TYPE_MUTATION,
  );

  return {
    loading,
    error,
    updateTicketType: async (
      id: string,
      input: Partial<SupportTicketTypeInput>,
    ) =>
      new Promise((resolve, reject) => {
        return updateType({
          variables: { id, input },
          update(cache, { data: { updateOemSupportTicketType: ref } = {} }) {
            const { currentUser } =
              cache.readQuery({
                query: CURRENT_USER_QUERY,
              }) || {};

            if (currentUser) {
              const supportTicketTypes = [
                ...currentUser.oem.supportTicketTypes,
              ];
              const index = supportTicketTypes.findIndex(
                (type) => type._id === id,
              );

              if (index >= 0) {
                supportTicketTypes.splice(index, 1, ref);
              }

              cache.writeQuery({
                query: CURRENT_USER_QUERY,
                data: {
                  currentUser: {
                    ...currentUser,
                    oem: {
                      ...currentUser.oem,
                      supportTicketTypes,
                    },
                  },
                },
              });
              evictSupportTicketCaches(cache);
            }
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};

export const useDeleteOemSupportTicketType = () => {
  const [remove, { loading, error }] = useMutation(
    DELETE_OEM_SUPPORT_TICKET_TYPE_MUTATION,
  );

  return {
    loading,
    error,
    deleteWorkOrderType: async (
      id: string,
      input?: DeleteSupportTicketTypeInput,
    ) => {
      return new Promise((resolve, reject) => {
        return remove({
          variables: { id, input },
          update(cache, { data: { deleteOemSupportTicketType: ref } }) {
            const { currentUser } =
              cache.readQuery({
                query: CURRENT_USER_QUERY,
              }) || {};

            if (currentUser && ref === "ok") {
              const supportTicketTypes = [
                ...currentUser.oem.supportTicketTypes,
              ];
              const index = supportTicketTypes.findIndex(
                (type) => type._id === id,
              );

              if (index >= 0) {
                supportTicketTypes.splice(index, 1);
              }

              cache.writeQuery({
                query: CURRENT_USER_QUERY,
                data: {
                  currentUser: {
                    ...currentUser,
                    oem: {
                      ...currentUser.oem,
                      supportTicketTypes,
                    },
                  },
                },
              });
              evictSupportTicketCaches(cache);
            }
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      });
    },
  };
};

export const useConfigureSupportTicketForm = () => {
  const [updateForm, { loading, error }] = useMutation(
    CONFIGURE_OEM_SUPPORT_TICKET_TYPE_MUTATION,
  );

  return {
    loading,
    error,
    configureForm: async (id: string, input: Record<string, unknown>) =>
      new Promise((resolve, reject) => {
        return updateForm({
          variables: { id, input },
          update(cache, { data: { configureOemSupportTicketType: ref } = {} }) {
            const { currentUser } =
              cache.readQuery({
                query: CURRENT_USER_QUERY,
              }) || {};

            if (currentUser) {
              const supportTicketTypes = [
                ...currentUser.oem.supportTicketTypes,
              ];
              const index = supportTicketTypes.findIndex(
                (type) => type._id === id,
              );

              if (index >= 0) {
                supportTicketTypes.splice(index, 1, ref);
              }

              cache.writeQuery({
                query: CURRENT_USER_QUERY,
                data: {
                  currentUser: {
                    ...currentUser,
                    oem: {
                      ...currentUser.oem,
                      supportTicketTypes,
                    },
                  },
                },
              });
              evictSupportTicketCaches(cache);
            }
          },
        })
          .then((data) => {
            resolve(data);
          })
          .catch((err) => {
            reject(errorsHandler(err));
          });
      }),
  };
};
